import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { useEmployeesQuery } from "modules/settings/hooks/useEmployeesQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { ReactElement, useEffect, useMemo, useState } from "react"
import { CustomersResponseDataSchema } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof CustomersResponseDataSchema>[number]
    })
  | undefined

type EmployeesFormSelectorProps = {
  onChange: (value: Option) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  buttonClassName?: string
  avoidDisplaySelected?: boolean
  value?: string | null
  icon?: ReactElement
}

const EmployeesFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  buttonClassName,
  avoidDisplaySelected,
  value,
  icon,
}: EmployeesFormSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data: employees, isPending } = useEmployeesQuery({
    query: {
      query: debouncedSearchQuery ?? undefined,
    },
  })

  const options = useMemo(() => {
    if (!employees) return []

    return employees.map((employee) => ({
      label: employee.email,
      value: employee.id,
      data: employee,
    }))
  }, [employees])

  function onComboboxChange(option: Option) {
    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange(option)
  }

  useEffect(() => {
    if (!value) {
      setSelectedValue(undefined)
    }
  }, [selectedValue, value])

  useEffect(() => {
    if (value && employees) {
      const result = options.find((e) => e.value === value)

      if (result) {
        setSelectedValue({ label: result.label, value: result.value })
      }
    }
  }, [value, employees, options])

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        shouldFilter={false}
        options={options}
        value={selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className={cn("w-full", buttonClassName)}
        icon={icon}
      />
    </div>
  )
}

export default EmployeesFormSelector
